import type { WavetableRuntimeTable } from "../types";
import { getBasicWavetable } from "./basicTables";
import { findWavetableCatalogEntry } from "./catalog";
import { parseSerumWav, parsedToRuntimeTable } from "./parseSerumWav";

const tableCache = new Map<string, WavetableRuntimeTable>();
const inflight = new Map<string, Promise<WavetableRuntimeTable>>();

export function getCachedWavetable(id: string): WavetableRuntimeTable | undefined {
  const basic = getBasicWavetable(id);
  if (basic) {
    return basic;
  }
  return tableCache.get(id);
}

export function cloneWavetableForTransfer(table: WavetableRuntimeTable): {
  table: WavetableRuntimeTable;
  transfer: Transferable[];
} {
  const samples = new Float32Array(table.samples.length);
  samples.set(table.samples);
  return {
    table: {
      id: table.id,
      frameSize: 2048,
      frameCount: table.frameCount,
      samples,
    },
    transfer: [samples.buffer],
  };
}

/**
 * Resolve a table id to runtime PCM.
 * Procedural basics are built in-process; file tables fetch from `assetsBaseUrl`.
 */
export async function loadWavetableRuntime(
  id: string,
  assetsBaseUrl = "/assets/",
): Promise<WavetableRuntimeTable> {
  const cached = getCachedWavetable(id);
  if (cached) {
    return cached;
  }
  const pending = inflight.get(id);
  if (pending) {
    return pending;
  }

  const job = (async () => {
    const basic = getBasicWavetable(id);
    if (basic) {
      return basic;
    }
    const entry = findWavetableCatalogEntry(id);
    if (!entry || entry.source !== "file" || !entry.path) {
      throw new Error(`Unknown wavetable id: ${id}`);
    }
    const base = assetsBaseUrl.endsWith("/") ? assetsBaseUrl : `${assetsBaseUrl}/`;
    const rel = entry.path.replace(/^\/+/, "").replace(/^assets\//, "");
    const encoded = rel.split("/").map(encodeURIComponent).join("/");
    const fetchUrl = new URL(encoded, base).href;
    const response = await fetch(fetchUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch wavetable ${id}: ${response.status}`);
    }
    const bytes = await response.arrayBuffer();
    const parsed = parseSerumWav(bytes);
    const table = parsedToRuntimeTable(id, parsed);
    tableCache.set(id, table);
    return table;
  })();

  inflight.set(id, job);
  try {
    return await job;
  } finally {
    inflight.delete(id);
  }
}

/** Cache a parsed table (tests / offline bounce from disk). */
export async function loadWavetableFromBytes(
  id: string,
  bytes: ArrayBuffer | Uint8Array,
): Promise<WavetableRuntimeTable> {
  const existing = getCachedWavetable(id);
  if (existing) {
    return existing;
  }
  const table = parsedToRuntimeTable(id, parseSerumWav(bytes));
  tableCache.set(id, table);
  return table;
}

export function clearWavetableCache(): void {
  tableCache.clear();
  inflight.clear();
}
